import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/Toast';
import { 
  Monitor, 
  Mouse, 
  Keyboard, 
  Upload, 
  Download, 
  Power, 
  PowerOff,
  RefreshCw,
  Users,
  Send,
  FileUp,
  X,
  Maximize2,
  Minimize2,
  Shield,
  Wifi,
  WifiOff,
  MessageCircle,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Play,
  Key,
  Copy,
  ExternalLink
} from 'lucide-react';

const API_URL = BACKEND_URL;

export const RemoteSupportPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  
  // Estado dos chamados
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  
  // Estado da sessão remota
  const [activeSession, setActiveSession] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [mouseEnabled, setMouseEnabled] = useState(false);
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // WebRTC
  const peerConnection = useRef(null);
  const dataChannel = useRef(null);
  const fileChannel = useRef(null);
  const screenRef = useRef(null);
  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  // Transferência de arquivos
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Carregar chamados
  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support/tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Erro ao carregar chamados:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
    const interval = setInterval(loadTickets, 10000);
    return () => clearInterval(interval);
  }, [loadTickets]);

  // Atualizar chamado selecionado
  useEffect(() => {
    if (selectedTicket) {
      const updated = tickets.find(t => t.id === selectedTicket.id);
      if (updated) {
        setSelectedTicket(updated);
      }
    }
  }, [tickets, selectedTicket?.id]);

  // Scroll para última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTicket?.messages]);

  // Enviar mensagem no chamado
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support/tickets/${selectedTicket.id}/messages`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: newMessage })
      });
      
      if (response.ok) {
        setNewMessage('');
        loadTickets();
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem');
    }
  };

  // Assumir chamado
  const takeTicket = async (ticket) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/support/tickets/${ticket.id}/take`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Chamado assumido!');
      loadTickets();
    } catch (error) {
      console.error('Erro ao assumir chamado:', error);
    }
  };

  // Resolver chamado
  const resolveTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/support/tickets/${selectedTicket.id}/resolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Chamado resolvido!');
      setSelectedTicket(null);
      disconnect();
      loadTickets();
    } catch (error) {
      console.error('Erro ao resolver chamado:', error);
    }
  };

  // Iniciar conexão remota
  const startRemoteConnection = async () => {
    if (!selectedTicket) return;
    
    try {
      toast.info('Solicitando conexão remota...');
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support/tickets/${selectedTicket.id}/connect`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveSession({ ...selectedTicket, ...data });
        initializeWebRTC(data);
        toast.success('Aguardando cliente aceitar...');
      } else {
        toast.error('Falha ao iniciar conexão');
      }
    } catch (error) {
      console.error('Erro na conexão:', error);
      toast.error('Erro ao conectar');
    }
  };

  // Inicializar WebRTC
  const initializeWebRTC = async (sessionData) => {
    try {
      const config = {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      };
      
      peerConnection.current = new RTCPeerConnection(config);
      
      dataChannel.current = peerConnection.current.createDataChannel('control', { ordered: true });
      fileChannel.current = peerConnection.current.createDataChannel('files', { ordered: true });
      
      dataChannel.current.onopen = () => {
        console.log('Canal de controle aberto');
        setIsConnected(true);
      };
      
      dataChannel.current.onclose = () => {
        console.log('Canal de controle fechado');
        setIsConnected(false);
      };
      
      dataChannel.current.onmessage = (event) => {
        handleControlMessage(JSON.parse(event.data));
      };
      
      fileChannel.current.onmessage = (event) => {
        handleFileMessage(event.data);
      };
      
      peerConnection.current.ontrack = (event) => {
        console.log('Stream recebido');
        if (screenRef.current && event.streams[0]) {
          screenRef.current.srcObject = event.streams[0];
          setIsViewing(true);
        }
      };
      
      peerConnection.current.onicecandidate = async (event) => {
        if (event.candidate) {
          await sendSignal('ice-candidate', event.candidate);
        }
      };
      
      const offer = await peerConnection.current.createOffer({
        offerToReceiveVideo: true,
        offerToReceiveAudio: false
      });
      
      await peerConnection.current.setLocalDescription(offer);
      await sendSignal('offer', offer);
      
      startSignalPolling(sessionData.id);
      
    } catch (error) {
      console.error('Erro WebRTC:', error);
      toast.error('Erro na conexão WebRTC');
    }
  };

  const sendSignal = async (type, data) => {
    if (!activeSession) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/remote-support/signal/${activeSession.id}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type, data })
      });
    } catch (error) {
      console.error('Erro ao enviar sinal:', error);
    }
  };

  const signalPollingRef = useRef(null);
  
  const startSignalPolling = (sessionId) => {
    signalPollingRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/remote-support/signals/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const signals = await response.json();
          for (const signal of signals) {
            await handleSignal(signal);
          }
        }
      } catch (error) {
        console.error('Erro no polling:', error);
      }
    }, 1000);
  };

  const handleSignal = async (signal) => {
    if (!peerConnection.current) return;
    
    try {
      if (signal.type === 'answer') {
        await peerConnection.current.setRemoteDescription(new RTCSessionDescription(signal.data));
      } else if (signal.type === 'ice-candidate') {
        await peerConnection.current.addIceCandidate(new RTCIceCandidate(signal.data));
      }
    } catch (error) {
      console.error('Erro ao processar sinal:', error);
    }
  };

  const handleControlMessage = (message) => {
    if (message.type === 'chat') {
      // Mensagem de chat via WebRTC
    } else if (message.type === 'file-request') {
      setPendingFiles(prev => [...prev, message.file]);
    }
  };

  const receivedChunks = useRef([]);
  const currentFileInfo = useRef(null);
  
  const handleFileMessage = (data) => {
    if (typeof data === 'string') {
      const info = JSON.parse(data);
      if (info.type === 'file-start') {
        currentFileInfo.current = info;
        receivedChunks.current = [];
        setDownloadProgress(0);
      } else if (info.type === 'file-end') {
        const blob = new Blob(receivedChunks.current);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = currentFileInfo.current.name;
        a.click();
        URL.revokeObjectURL(url);
        setDownloadProgress(100);
        toast.success(`Arquivo ${currentFileInfo.current.name} recebido!`);
      }
    } else {
      receivedChunks.current.push(data);
      if (currentFileInfo.current) {
        const progress = (receivedChunks.current.length * 16384 / currentFileInfo.current.size) * 100;
        setDownloadProgress(Math.min(progress, 99));
      }
    }
  };

  const handleMouseEvent = (event) => {
    if (!mouseEnabled || !dataChannel.current || dataChannel.current.readyState !== 'open') return;
    
    const rect = screenRef.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    
    dataChannel.current.send(JSON.stringify({
      type: 'mouse',
      event: event.type,
      x, y,
      button: event.button
    }));
  };

  const handleKeyEvent = (event) => {
    if (!keyboardEnabled || !dataChannel.current || dataChannel.current.readyState !== 'open') return;
    
    event.preventDefault();
    dataChannel.current.send(JSON.stringify({
      type: 'keyboard',
      event: event.type,
      key: event.key,
      code: event.code,
      ctrlKey: event.ctrlKey,
      altKey: event.altKey,
      shiftKey: event.shiftKey
    }));
  };

  const sendFile = async (file) => {
    if (!fileChannel.current || fileChannel.current.readyState !== 'open') {
      toast.error('Canal de arquivos não está pronto');
      return;
    }
    
    setUploadProgress(0);
    
    fileChannel.current.send(JSON.stringify({
      type: 'file-start',
      name: file.name,
      size: file.size,
      mimeType: file.type
    }));
    
    const chunkSize = 16384;
    const reader = new FileReader();
    let offset = 0;
    
    const readNextChunk = () => {
      const slice = file.slice(offset, offset + chunkSize);
      reader.readAsArrayBuffer(slice);
    };
    
    reader.onload = () => {
      fileChannel.current.send(reader.result);
      offset += chunkSize;
      setUploadProgress((offset / file.size) * 100);
      
      if (offset < file.size) {
        readNextChunk();
      } else {
        fileChannel.current.send(JSON.stringify({ type: 'file-end' }));
        toast.success(`Arquivo ${file.name} enviado!`);
        setUploadProgress(100);
      }
    };
    
    readNextChunk();
  };

  const disconnect = () => {
    if (signalPollingRef.current) {
      clearInterval(signalPollingRef.current);
    }
    
    if (dataChannel.current) dataChannel.current.close();
    if (fileChannel.current) fileChannel.current.close();
    if (peerConnection.current) peerConnection.current.close();
    
    setIsConnected(false);
    setIsViewing(false);
    setActiveSession(null);
    setMouseEnabled(false);
    setKeyboardEnabled(false);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    
    if (!isFullscreen) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  // Status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'aberto':
        return <Badge className="bg-amber-500"><AlertCircle className="w-3 h-3 mr-1" />Aberto</Badge>;
      case 'em_atendimento':
        return <Badge className="bg-blue-500"><Clock className="w-3 h-3 mr-1" />Em Atendimento</Badge>;
      case 'resolvido':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Resolvido</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Verificar se é super admin
  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Acesso Restrito</h2>
            <p className="text-slate-500">Este módulo é exclusivo para administradores.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-4 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {selectedTicket && (
            <Button variant="ghost" size="sm" onClick={() => { setSelectedTicket(null); disconnect(); }}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Voltar
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageCircle className="w-6 h-6" />
              {selectedTicket ? `Chamado #${selectedTicket.id.slice(0,8)}` : 'Central de Suporte'}
            </h1>
            <p className="text-slate-500">
              {selectedTicket ? selectedTicket.tenant_name : 'Gerencie chamados e acesse clientes remotamente'}
            </p>
          </div>
        </div>
        
        {activeSession && (
          <Button variant="destructive" onClick={disconnect}>
            <PowerOff className="w-4 h-4 mr-2" />
            Desconectar
          </Button>
        )}
      </div>

      <div className="flex-1 flex gap-4 overflow-hidden">
        {/* Lista de Chamados ou Detalhe */}
        {!selectedTicket ? (
          <Card className="flex-1">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Chamados de Suporte
                  </CardTitle>
                  <CardDescription>
                    {tickets.filter(t => t.status !== 'resolvido').length} chamados pendentes
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={loadTickets}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin" />
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum chamado de suporte</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        ticket.status === 'resolvido' ? 'bg-slate-50 opacity-60' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => setSelectedTicket(ticket)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium">{ticket.tenant_name}</p>
                          <p className="text-sm text-slate-500">{ticket.user_email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {ticket.remote_id && (
                            <Badge className="bg-green-500" title="Acesso remoto disponível">
                              <Key className="w-3 h-3 mr-1" />
                              RustDesk
                            </Badge>
                          )}
                          {getStatusBadge(ticket.status)}
                        </div>
                      </div>
                      
                      <p className="text-sm bg-slate-100 p-2 rounded mt-2 line-clamp-2">
                        {ticket.problem_description}
                      </p>
                      
                      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                        <span>{new Date(ticket.created_at).toLocaleString()}</span>
                        <span>{ticket.messages?.length || 0} mensagens</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Detalhe do Chamado + Chat */}
            <Card className="w-96 flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Conversa</CardTitle>
                  {getStatusBadge(selectedTicket.status)}
                </div>
                <div className="p-2 bg-amber-50 rounded text-sm mt-2">
                  <span className="font-medium">Problema:</span> {selectedTicket.problem_description}
                </div>
                
                {/* Dados de Acesso Remoto */}
                {selectedTicket.remote_id && (
                  <div className="p-3 bg-green-50 rounded-lg mt-2 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Key className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Acesso Remoto (RustDesk)</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-xs text-slate-500">ID:</span>
                        <div className="flex items-center gap-1">
                          <code className="bg-white px-2 py-1 rounded font-mono text-green-700">
                            {selectedTicket.remote_id}
                          </code>
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText(selectedTicket.remote_id);
                              toast.success('ID copiado!');
                            }}
                            className="p-1 hover:bg-green-100 rounded"
                          >
                            <Copy className="w-3 h-3 text-green-600" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500">Senha:</span>
                        <div className="flex items-center gap-1">
                          <code className="bg-white px-2 py-1 rounded font-mono text-green-700">
                            {selectedTicket.remote_password || '***'}
                          </code>
                          {selectedTicket.remote_password && (
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(selectedTicket.remote_password);
                                toast.success('Senha copiada!');
                              }}
                              className="p-1 hover:bg-green-100 rounded"
                            >
                              <Copy className="w-3 h-3 text-green-600" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <a 
                      href="https://rustdesk.com/download"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-green-600 hover:underline mt-2"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Abrir RustDesk
                    </a>
                  </div>
                )}
              </CardHeader>
              
              <CardContent className="flex-1 flex flex-col p-2 overflow-hidden">
                {/* Mensagens */}
                <ScrollArea className="flex-1 pr-2">
                  <div className="space-y-2 p-2">
                    {selectedTicket.messages?.length === 0 ? (
                      <p className="text-center text-sm text-slate-400 py-4">
                        Nenhuma mensagem ainda. Responda ao cliente!
                      </p>
                    ) : (
                      selectedTicket.messages?.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`p-2 rounded-lg text-sm ${
                            msg.from === 'support' 
                              ? 'bg-blue-100 ml-4' 
                              : 'bg-slate-100 mr-4'
                          }`}
                        >
                          <p className="text-xs font-medium text-slate-500 mb-1">
                            {msg.sender_name}
                          </p>
                          <p>{msg.text}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                
                {/* Input de mensagem */}
                <div className="border-t pt-2 mt-2">
                  <div className="flex gap-2">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Digite sua resposta..."
                      className="min-h-[60px]"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button className="flex-1" onClick={sendMessage} disabled={!newMessage.trim()}>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar
                    </Button>
                  </div>
                </div>
                
                {/* Ações */}
                <div className="border-t pt-3 mt-3 space-y-2">
                  {selectedTicket.status !== 'resolvido' && (
                    <>
                      {selectedTicket.remote_id ? (
                        <div className="text-center text-sm text-slate-500 py-2">
                          Use o RustDesk com os dados acima para acessar a máquina do cliente
                        </div>
                      ) : (
                        <div className="text-center text-sm text-amber-600 py-2 bg-amber-50 rounded">
                          Cliente não informou dados do RustDesk. Solicite via chat.
                        </div>
                      )}
                      
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={resolveTicket}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Marcar como Resolvido
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Instruções de Acesso Remoto */}
            <Card className="flex-1 flex flex-col">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Monitor className="w-5 h-5" />
                  Como Acessar Remotamente
                </CardTitle>
              </CardHeader>
              
              <CardContent className="flex-1">
                {selectedTicket.remote_id ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h3 className="font-medium text-green-700 mb-3">✅ Cliente forneceu acesso RustDesk</h3>
                      
                      <ol className="space-y-3 text-sm">
                        <li className="flex items-start gap-2">
                          <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
                          <span>Abra o <strong>RustDesk</strong> no seu computador</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
                          <div>
                            <span>Digite o ID: </span>
                            <code className="bg-white px-2 py-1 rounded font-mono font-bold">{selectedTicket.remote_id}</code>
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(selectedTicket.remote_id);
                                toast.success('ID copiado!');
                              }}
                              className="ml-2 text-green-600 hover:text-green-800"
                            >
                              <Copy className="w-4 h-4 inline" />
                            </button>
                          </div>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
                          <div>
                            <span>Digite a senha: </span>
                            <code className="bg-white px-2 py-1 rounded font-mono font-bold">{selectedTicket.remote_password}</code>
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(selectedTicket.remote_password);
                                toast.success('Senha copiada!');
                              }}
                              className="ml-2 text-green-600 hover:text-green-800"
                            >
                              <Copy className="w-4 h-4 inline" />
                            </button>
                          </div>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">4</span>
                          <span>Clique em <strong>Conectar</strong> e acesse a máquina do cliente!</span>
                        </li>
                      </ol>
                    </div>
                    
                    <a 
                      href="https://rustdesk.com/download"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 p-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Baixar RustDesk (Gratuito)
                    </a>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                      <h3 className="font-medium text-amber-700 mb-3">⚠️ Cliente não forneceu acesso remoto</h3>
                      
                      <p className="text-sm text-amber-600 mb-3">
                        Para acessar a máquina do cliente, solicite que ele:
                      </p>
                      
                      <ol className="space-y-2 text-sm text-amber-700">
                        <li>1. Baixe o <strong>RustDesk</strong> (gratuito)</li>
                        <li>2. Abra o programa</li>
                        <li>3. Envie o <strong>ID</strong> e <strong>Senha</strong> pelo chat</li>
                      </ol>
                    </div>
                    
                    <div className="p-3 bg-slate-50 rounded-lg text-sm">
                      <p className="font-medium mb-2">Mensagem sugerida para enviar:</p>
                      <p className="text-slate-600 italic">
                        "Para eu acessar sua máquina, por favor baixe o RustDesk em rustdesk.com/download, 
                        abra o programa e me envie o ID e a Senha que aparecem na tela."
                      </p>
                      <button 
                        onClick={() => {
                          setNewMessage("Para eu acessar sua máquina, por favor baixe o RustDesk em rustdesk.com/download, abra o programa e me envie o ID e a Senha que aparecem na tela.");
                        }}
                        className="mt-2 text-blue-600 hover:underline text-xs"
                      >
                        Usar esta mensagem
                      </button>
                    </div>
                    
                    <a 
                      href="https://rustdesk.com/download"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 p-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Link para enviar ao cliente
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default RemoteSupportPage;
